import { formatPhone } from '@/lib/phone';
import { formatILS, formatPercent } from '@/lib/money';
import { formatHours } from '@/features/schedule/hours';
import type { EmployeeListItem } from './api';

/**
 * אילו עמודות מוצגות בטבלת העובדים.
 *
 * לא לכל מנהל מעניין אותו הדבר. מנהל כוח אדם רוצה סטטוס והרשאות,
 * ומנהל מכירות רוצה לדעת מי עומד ביעד ומי לא. במקום טבלה אחת
 * שמנסה לרצות את שניהם ולא קריאה לאיש, כל אחד בוחר.
 *
 * השם תמיד מוצג, ולכן אינו ברשימה: טבלה בלי שם אינה טבלת עובדים.
 */
export type ColumnKey =
  | 'phone'
  | 'branch'
  | 'jobTitle'
  | 'role'
  | 'status'
  | 'comax'
  | 'salesProgress'
  | 'hours';

/** שעות משובצות החודש מול המכסה, לעמודת השעות. */
export type EmployeeHours = {
  minutes: number;
  shifts: number;
  targetHours: number | null;
  isOverride: boolean;
};

export type EmployeeSales = {
  sold: number;
  target: number | null;
  percent: number | null;
};

export const COLUMN_LABELS: Record<ColumnKey, string> = {
  phone: 'טלפון',
  branch: 'סניף',
  jobTitle: 'תפקיד',
  role: 'הרשאות',
  status: 'סטטוס',
  comax: 'קוד עובד',
  salesProgress: 'התקדמות ליעד',
  hours: 'שעות החודש',
};

/** ברירת המחדל: מה שהיה במסך עד היום. */
export const DEFAULT_COLUMNS: ColumnKey[] = ['phone', 'branch', 'jobTitle', 'role', 'status'];

export const SALES_COLUMNS: ColumnKey[] = ['salesProgress'];

/**
 * התווית שמתחת לסרגל השעות. כמו במכירות: הסרגל לבד הוא צבע בלי
 * מילה, ולכן המספרים הם חלק ממנו ולא קישוט (כלל 10).
 *
 * בלי מכסה אין במה למדוד, ולכן מוצגות רק השעות.
 */
export function hoursLabel(hours: EmployeeHours | undefined): string {
  if (!hours || (hours.minutes === 0 && hours.targetHours === null)) {
    return 'לא שובץ החודש';
  }
  if (hours.targetHours === null) {
    return `${formatHours(hours.minutes)} · ללא מכסה`;
  }
  const over = hours.minutes > hours.targetHours * 60;
  return `${formatHours(hours.minutes)} מתוך ${hours.targetHours}${
    over ? ` · חריגה של ${formatHours(hours.minutes - hours.targetHours * 60)}` : ''
  }${hours.isOverride ? ' · מכסה לחודש הזה' : ''}`;
}

/**
 * התווית שמתחת לסרגל. הסרגל לבד הוא צבע בלי מילה, ולכן המספרים
 * הם חלק ממנו ולא קישוט (כלל 10).
 */
export function salesLabel(sales: EmployeeSales | undefined): string {
  if (!sales || (sales.sold === 0 && sales.target === null)) return 'אין נתוני מכירות';
  if (sales.target === null) return `${formatILS(sales.sold)} · ללא יעד`;
  return `${formatILS(sales.sold)} מתוך ${formatILS(sales.target)} · ${formatPercent(sales.percent)}`;
}

const STORAGE_KEY = 'employees-columns';

export function readColumns(): ColumnKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_COLUMNS;
    const valid = parsed.filter(
      (key): key is ColumnKey => typeof key === 'string' && key in COLUMN_LABELS,
    );
    return valid;
  } catch {
    return DEFAULT_COLUMNS;
  }
}

export function saveColumns(columns: ColumnKey[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // חלון פרטי. הבחירה פשוט לא תיזכר.
  }
}

/** עמודות המכירות אינן כאן: הן סרגל ולא טקסט (ראו EmployeeCell). */
export function cellValue(key: ColumnKey, employee: EmployeeListItem): string {
  switch (key) {
    case 'phone':
      return formatPhone(employee.phone);
    case 'jobTitle':
      return employee.job_titles?.name ?? '—';
    case 'role':
      return employee.roles?.name ?? '—';
    case 'comax':
      return employee.comax_employee_code ?? '—';
    default:
      return '—';
  }
}
